import { ReactNode, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
} from '@mui/material';
import { useNavigate } from 'react-router';
import { useMutation } from 'react-query';
import { archivosFetch } from '../../utils/fetch';
import { handleDownload } from '../../utils/download';
import { BRAND_DIALOG_CLASS } from '../../theme';
import { DocumentAction } from './RowActions';
import { HomeDocument } from './types';

// The four per-row actions of the documents table (view, edit,
// download, delete) with what they entail: the delete confirmation and the
// result notice. Shared by the Home and Résultats.
//
// `onAction` is given to the table or the cards; `elements` is painted once
// on the page (dialog + snackbar). `notify` lets the page show its
// own notices through the same snackbar.

export type Notice = {
  severity: 'success' | 'error';
  text: string;
};

const GENERIC_ERROR =
  "L'opération n'a pas pu aboutir. Veuillez réessayer dans quelques instants.";

type Options = {
  /** After a deletion confirmed and accepted by the API. */
  onDeleted: (document: HomeDocument) => void;
  /** After a 404 on delete: the document no longer exists, better to reload. */
  onVanished?: (document: HomeDocument) => void;
  initialNotice?: Notice;
};

export const useDocumentActions = ({
  onDeleted,
  onVanished,
  initialNotice,
}: Options) => {
  const navigate = useNavigate();
  const [toBeDeleted, setToBeDeleted] = useState<HomeDocument>();
  const [notice, setNotice] = useState<Notice | undefined>(initialNotice);

  const { mutateAsync: deleteDocument, isLoading: isDeleting } = useMutation(
    (id: string) =>
      archivosFetch(
        `${process.env.REACT_APP_API_URL}/api/archives/${id}/delete-document`,
        { method: 'DELETE' },
      ),
  );

  const onAction = async (action: DocumentAction, document: HomeDocument) => {
    switch (action) {
      case 'view':
        navigate(`/document/${document.id}`);
        break;
      case 'edit':
        navigate(`/document/edit/${document.id}`);
        break;
      case 'download': {
        const failure = await handleDownload(document.id);
        if (failure) setNotice({ severity: 'error', text: failure });
        break;
      }
      case 'delete':
        setToBeDeleted(document);
        break;
      default:
        break;
    }
  };

  const confirmDelete = async () => {
    if (!toBeDeleted) return;

    try {
      const response = await deleteDocument(toBeDeleted.id);

      if (response.ok) {
        setNotice({ severity: 'success', text: 'Document supprimé.' });
        onDeleted(toBeDeleted);
      } else if (response.status === 403) {
        setNotice({
          severity: 'error',
          text: "Vous n'avez pas les droits pour cette action.",
        });
      } else if (response.status === 404) {
        setNotice({ severity: 'error', text: "Ce document n'existe plus." });
        onVanished?.(toBeDeleted);
      } else {
        setNotice({ severity: 'error', text: GENERIC_ERROR });
      }
    } catch (_) {
      setNotice({ severity: 'error', text: GENERIC_ERROR });
    }

    setToBeDeleted(undefined);
  };

  const elements: ReactNode = (
    <>
      {/* Delete confirmation, with the XD modal pattern */}
      <Dialog
        className={BRAND_DIALOG_CLASS}
        open={Boolean(toBeDeleted)}
        onClose={() => !isDeleting && setToBeDeleted(undefined)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Supprimer le document</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer le document «{' '}
            {toBeDeleted?.title} » ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setToBeDeleted(undefined)}
            disabled={isDeleting}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={confirmDelete}
            disabled={isDeleting}
            autoFocus
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(notice)}
        autoHideDuration={6000}
        disableWindowBlurListener
        onClose={() => setNotice(undefined)}
      >
        <Alert
          severity={notice?.severity ?? 'success'}
          variant="filled"
          onClose={() => setNotice(undefined)}
          sx={{ width: '100%' }}
        >
          {notice?.text}
        </Alert>
      </Snackbar>
    </>
  );

  return { onAction, notify: setNotice, elements };
};
